import { useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Search, FlaskConical, Plus, Check } from "lucide-react";
import { api } from "../api";
import { useEnquiry } from "../context/EnquiryContext";
import { scrollToId } from "./Header";
import CompoundModal from "./CompoundModal";
import Reveal from "./Reveal";
import { toast } from "sonner";

export default function Catalog() {
    const [products, setProducts] = useState([]);
    const [categories, setCategories] = useState([]);
    const [activeCat, setActiveCat] = useState("All");
    const [q, setQ] = useState("");
    const [openSlug, setOpenSlug] = useState(null);
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState(false);
    const { has, add } = useEnquiry();

    const loadCatalog = () => {
        setLoading(true);
        setLoadError(false);
        Promise.all([api.get("/products"), api.get("/categories")])
            .then(([p, c]) => {
                setProducts(p.data);
                setCategories(c.data);
            })
            .catch(() => {
                setLoadError(true);
                toast.error("Could not load the catalog");
            })
            .finally(() => setLoading(false));
    };

    useEffect(() => {
        loadCatalog();
    }, []);

    const filtered = useMemo(() => {
        return products.filter((p) => {
            const catOk = activeCat === "All" || p.category === activeCat;
            const needle = q.trim().toLowerCase();
            const qOk = !needle || [p.name, p.tagline, p.cas, p.category].filter(Boolean).join(" ").toLowerCase().includes(needle);
            return catOk && qOk;
        });
    }, [products, activeCat, q]);

    const openProduct = products.find((p) => p.slug === openSlug);

    return (
        <section id="catalog" data-testid="catalog-section" className="py-16 md:py-24 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto">
            <Reveal>
                <div className="flex items-end justify-between flex-wrap gap-6 mb-10">
                    <div>
                        <div className="font-jbm text-xs uppercase tracking-[0.25em] text-cyan-700 mb-3">01 — Compound catalog</div>
                        <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight text-slate-900">
                            Reference compounds, batch-verified
                        </h2>
                    </div>
                    <p className="max-w-sm text-sm leading-relaxed text-slate-500">
                        Every entry links to its specification sheet. Certificates of Analysis are issued per batch and
                        supplied to verified institutions on enquiry.
                    </p>
                </div>
                <div className="mt-5 inline-flex items-center gap-2.5 bg-amber-50 border border-amber-300 rounded-full px-5 py-2.5" data-testid="catalog-min-order-notice">
                    <span className="w-2 h-2 rounded-full bg-amber-500 shrink-0" />
                    <span className="text-xs font-semibold tracking-wide text-amber-800">
                        Minimum order: 10 vials of each peptide requested
                    </span>
                </div>
            </Reveal>

            <Reveal delay={0.1}>
                <div className="sticky top-16 md:top-20 z-30 bg-[#F8FAFC]/90 backdrop-blur-md py-4 -mx-4 px-4 sm:mx-0 sm:px-0 rounded-xl">
                    <div className="relative mb-4 max-w-md">
                        <Search className="w-4 h-4 absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
                        <input
                            data-testid="catalog-search-input"
                            value={q}
                            onChange={(e) => setQ(e.target.value)}
                            placeholder="Search compound, CAS number, category…"
                            className="w-full bg-white border border-slate-200 rounded-full pl-11 pr-4 py-3 text-sm outline-none focus:border-cyan-700 focus:ring-2 focus:ring-cyan-100 transition-all duration-300"
                        />
                    </div>
                    <div className="flex gap-2 overflow-x-auto pb-1" data-testid="catalog-category-filters">
                        {["All", ...categories].map((c) => (
                            <button
                                key={c}
                                data-testid={`filter-pill-${c.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                                onClick={() => setActiveCat(c)}
                                className={`shrink-0 font-jbm text-xs px-4 py-2 rounded-full border transition-colors duration-300 ${
                                    activeCat === c
                                        ? "bg-slate-900 text-white border-slate-900"
                                        : "bg-white text-slate-600 border-slate-200 hover:border-slate-400"
                                }`}
                            >
                                {c}
                            </button>
                        ))}
                    </div>
                </div>
            </Reveal>

            {loading ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mt-8" data-testid="catalog-loading" aria-label="Loading compounds">
                    {[...Array(6)].map((_, i) => (
                        <div key={i} className="bg-white border border-slate-200 rounded-2xl p-6">
                            <div className="skeleton rounded-xl aspect-square mb-5" />
                            <div className="skeleton h-3 w-24 rounded-full mb-3" />
                            <div className="skeleton h-4 w-3/4 rounded-full mb-2" />
                            <div className="skeleton h-4 w-1/2 rounded-full" />
                        </div>
                    ))}
                </div>
            ) : (
                <motion.div layout className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mt-8" data-testid="catalog-grid">
                    <AnimatePresence mode="popLayout">
                        {filtered.map((p, i) => {
                            const inEnquiry = has(p.slug);
                            return (
                                <motion.article
                                    layout
                                    key={p.slug}
                                    initial={{ opacity: 0, y: 24 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    exit={{ opacity: 0, scale: 0.96 }}
                                    transition={{ duration: 0.5, delay: Math.min(i * 0.04, 0.4), ease: [0.22, 1, 0.36, 1] }}
                                    data-testid={`compound-card-${p.slug}`}
                                    className="group bg-white border border-slate-200 rounded-2xl p-6 flex flex-col hover:border-cyan-700/50 hover:shadow-[0_24px_50px_-20px_rgba(8,145,178,0.25)] hover:-translate-y-1 transition-all duration-400 cursor-pointer"
                                    onClick={() => setOpenSlug(p.slug)}
                                >
                                    <div className="relative rounded-xl overflow-hidden mb-5 border border-slate-100 bg-white [container-type:inline-size]">
                                        <img
                                            src="/vial.jpg"
                                            alt={`${p.name} research vial`}
                                            loading="lazy"
                                            className="w-full aspect-square object-cover transition-transform duration-700 group-hover:scale-[1.03]"
                                        />
                                        <div className="absolute left-1/2 -translate-x-1/2 top-[64.2%] w-[33%] text-center pointer-events-none">
                                            <span className="block font-display font-bold uppercase text-[#1a2433] leading-[1.15] tracking-[0.06em] [font-size:3cqw]">
                                                {p.name}
                                            </span>
                                        </div>
                                    </div>
                                    <div className="font-jbm text-[10px] uppercase tracking-widest text-cyan-700 mb-3">{p.category}</div>
                                    <p className="text-sm text-slate-500 leading-relaxed">{p.tagline}</p>

                                    <div className="mt-5 pt-4 border-t border-slate-100 grid grid-cols-2 gap-y-2 text-xs">
                                        <span className="font-jbm text-slate-400">PURITY</span>
                                        <span className="font-jbm text-slate-800 text-right">{p.purity}</span>
                                        <span className="font-jbm text-slate-400">SIZES</span>
                                        <span className="font-jbm text-slate-800 text-right truncate" title={p.strengths ? p.strengths.join(" / ") : "On enquiry"}>
                                            {p.strengths ? p.strengths.join(" / ") : "On enquiry"}
                                        </span>
                                        <span className="font-jbm text-slate-400">FORM</span>
                                        <span className="font-jbm text-slate-800 text-right truncate">{p.form}</span>
                                        {p.cas && (
                                            <>
                                                <span className="font-jbm text-slate-400">CAS</span>
                                                <span className="font-jbm text-slate-800 text-right">{p.cas}</span>
                                            </>
                                        )}
                                    </div>

                                    <div className="mt-5 flex gap-2">
                                        <button
                                            data-testid={`view-spec-${p.slug}`}
                                            className="flex-1 flex items-center justify-center gap-2 text-sm font-medium border border-slate-200 rounded-full py-2.5 text-slate-700 group-hover:border-cyan-700 group-hover:text-cyan-800 transition-colors duration-300"
                                        >
                                            <FlaskConical className="w-4 h-4" />
                                            Specification
                                        </button>
                                        <button
                                            data-testid={`add-enquiry-${p.slug}`}
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                if (inEnquiry) {
                                                    scrollToId("#enquiry");
                                                } else {
                                                    add(p.slug, p.strengths?.[0] || null);
                                                    toast.success(`${p.name} added to your enquiry`);
                                                }
                                            }}
                                            aria-label={inEnquiry ? "View enquiry" : `Add ${p.name} to enquiry`}
                                            className={`w-11 h-11 shrink-0 flex items-center justify-center rounded-full border transition-colors duration-300 ${
                                                inEnquiry
                                                    ? "bg-cyan-700 border-cyan-700 text-white"
                                                    : "border-slate-200 text-slate-600 hover:bg-slate-900 hover:border-slate-900 hover:text-white"
                                            }`}
                                        >
                                            {inEnquiry ? <Check className="w-4 h-4" /> : <Plus className="w-4 h-4" />}
                                        </button>
                                    </div>
                                </motion.article>
                            );
                        })}
                    </AnimatePresence>
                </motion.div>
            )}

            {!loading && loadError && (
                <div className="py-24 text-center" data-testid="catalog-error">
                    <p className="font-display text-xl text-slate-700">The catalog couldn't be loaded.</p>
                    <p className="text-sm text-slate-500 mt-2">The server may still be starting up — this usually resolves in a few seconds.</p>
                    <button
                        data-testid="catalog-retry-button"
                        onClick={loadCatalog}
                        className="mt-6 font-medium px-7 py-3 rounded-full bg-slate-900 text-white hover:bg-cyan-800 transition-colors duration-300"
                    >
                        Retry
                    </button>
                </div>
            )}

            {!loading && !loadError && filtered.length === 0 && (
                <div className="py-24 text-center" data-testid="catalog-empty">
                    <p className="font-display text-xl text-slate-700">No compounds match your search.</p>
                    <p className="text-sm text-slate-500 mt-2">Try a different name, CAS number or category.</p>
                </div>
            )}

            <CompoundModal product={openProduct} onClose={() => setOpenSlug(null)} />
        </section>
    );
}
